import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { getReport, updateReport, publishReport, unpublishReport, generateAi, uploadFinalPdf, deleteFinalPdf, getGroupPreviewHtml, generateAiForGroup, getGroupMarkdown } from '../../Services/EditableReportService';
import { marked } from 'marked';
import { EditableReport, EditableReportContent, Citation, CriteriaItem, IdeaItem } from '../../Types/EditableReport.types';
import { useHeaderVisibility } from '../../Context/HeaderVisibilityContext';

console.log('✅ AdminReportEditor.tsx loaded - v2');

// Estilos exactos del PDF de Plano Invisible
const reportStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  .report-canvas {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    max-width: 900px;
    margin: 0 auto;
    padding: 15px 50px 40px 50px;
    background: white;
    color: #1a1a1a;
    line-height: 1.6;
    font-size: 14px;
    min-height: 100vh;
    box-shadow: 0 0 30px rgba(0,0,0,0.1);
  }

  /* Header - Top bar with logo */
  .report-header-top {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 20px;
  }

  .plano-logo {
    text-align: right;
  }

  .plano-logo img {
    height: 180px;
    width: auto;
  }

  /* Header - Title section */
  .report-header-title {
    text-align: center;
    margin-top: 40px;
    margin-bottom: 30px;
  }

  .report-header-title h1 {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: 4px;
    color: #1a1a1a;
    margin: 0 0 25px 0;
    text-transform: uppercase;
  }

  .report-header-title .family-name {
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    line-height: 1.3;
  }

  /* Brain house image */
  .brain-house-container {
    text-align: center;
    margin: 40px 0 0 0;
  }

  .brain-house-container img {
    max-width: 420px;
    height: auto;
  }

  /* Header disclaimer */
  .header-disclaimer {
    text-align: center;
    padding: 80px 0 50px 0;
    font-size: 11px;
    line-height: 1.6;
    color: #666;
  }

  /* Personality section */
  .personality-section {
    background-color: #E59B3C;
    padding: 40px 50px;
    text-align: center;
    position: relative;
  }

  .personality-section .personality-content {
    color: #1a1a1a;
    font-size: 14px;
    line-height: 1.7;
  }

  .personality-section .personality-content strong,
  .personality-section .personality-content b {
    font-weight: 800;
  }

  .personality-section .personality-content.editable:hover {
    background: rgba(255,255,255,0.1);
  }

  .personality-section .personality-content.editable:focus {
    background: rgba(255,255,255,0.2);
  }

  /* Format toolbar */
  .format-toolbar {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .personality-section:hover .format-toolbar,
  .personality-section:focus-within .format-toolbar {
    opacity: 1;
  }

  .format-toolbar button {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 4px;
    background: rgba(255,255,255,0.8);
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
  }

  .format-toolbar button:hover {
    background: white;
  }

  .format-toolbar button.italic {
    font-style: italic;
  }

  /* Key Points Introduction */
  .keypoints-intro {
    text-align: left;
    padding: 100px 0 40px 0;
    font-size: 14px;
    line-height: 1.6;
    color: #1a1a1a;
  }

  /* Criteria Module */
  .criteria-module {
    display: flex;
    gap: 40px;
    padding: 30px 0;
    border-top: 1px solid #1a1a1a;
    border-bottom: 1px solid #1a1a1a;
    margin-bottom: -1px;
    position: relative;
  }

  .criteria-module:hover .criteria-drag-handle,
  .criteria-module:hover .criteria-delete {
    opacity: 1;
  }

  .criteria-drag-handle {
    position: absolute;
    left: -30px;
    top: 30px;
    cursor: grab;
    color: #ccc;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .criteria-delete {
    position: absolute;
    right: -30px;
    top: 30px;
    cursor: pointer;
    color: #ccc;
    opacity: 0;
    transition: opacity 0.2s;
    background: none;
    border: none;
    font-size: 16px;
  }

  .criteria-delete:hover {
    color: #c62828;
  }

  .criteria-left {
    flex: 0 0 35%;
  }

  .criteria-title {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 10px;
    color: #1a1a1a;
  }

  .criteria-title .orange {
    color: #E59B3C;
  }

  .criteria-subtitle {
    font-size: 18px;
    letter-spacing: 2px;
    color: #666;
    text-transform: uppercase;
    font-style: italic;
  }

  .criteria-right {
    flex: 1;
    font-size: 13px;
    line-height: 1.7;
    color: #333;
    text-align: justify;
  }

  .criteria-right strong,
  .criteria-right b {
    font-weight: 800;
  }

  .criteria-toolbar {
    position: absolute;
    top: 8px;
    right: 40px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .criteria-module:hover .criteria-toolbar,
  .criteria-module:focus-within .criteria-toolbar {
    opacity: 1;
  }

  .criteria-toolbar button {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: #f0f0f0;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  .criteria-toolbar button:hover {
    background: #e0e0e0;
  }

  .criteria-toolbar button.color-orange {
    background: #E59B3C;
    color: white;
  }

  .criteria-toolbar button.color-black {
    background: #1a1a1a;
    color: white;
  }

  .criteria-toolbar button.btn-cite {
    background: #6c5ce7;
    color: white;
    margin-left: 4px;
  }

  .criteria-toolbar button.btn-cite:hover {
    background: #5b4cdb;
  }

  .add-criteria-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 20px;
    margin-top: 20px;
    border: 2px dashed #ddd;
    border-radius: 4px;
    background: transparent;
    color: #888;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
  }

  .add-criteria-btn:hover {
    border-color: #E59B3C;
    color: #E59B3C;
  }

  /* Ideas Section */
  .ideas-section {
    margin-top: 60px;
  }

  .ideas-section-title {
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid #1a1a1a;
  }

  .idea-module {
    background: #e8e8e8;
    padding: 40px 50px;
    margin-bottom: 20px;
    position: relative;
  }

  .idea-module:hover .idea-drag-handle,
  .idea-module:hover .idea-delete {
    opacity: 1;
  }

  .idea-drag-handle {
    position: absolute;
    left: -25px;
    top: 25px;
    cursor: grab;
    color: #ccc;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .idea-delete {
    position: absolute;
    right: -25px;
    top: 25px;
    cursor: pointer;
    color: #ccc;
    opacity: 0;
    transition: opacity 0.2s;
    background: none;
    border: none;
    font-size: 16px;
  }

  .idea-delete:hover {
    color: #c62828;
  }

  .idea-title {
    font-size: 31px;
    font-weight: 500;
    margin-bottom: 12px;
    color: #1a1a1a;
    letter-spacing: -1px;
  }

  .idea-text {
    font-size: 13px;
    line-height: 1.7;
    color: #333;
  }

  .idea-toolbar {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .idea-module:hover .idea-toolbar,
  .idea-module:focus-within .idea-toolbar {
    opacity: 1;
  }

  .idea-toolbar button {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: #e0e0e0;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  .idea-toolbar button:hover {
    background: #d0d0d0;
  }

  .idea-toolbar button.color-orange {
    background: #E59B3C;
    color: white;
  }

  .idea-toolbar button.color-black {
    background: #1a1a1a;
    color: white;
  }

  .idea-toolbar button.btn-cite {
    background: #6c5ce7;
    color: white;
  }

  .add-idea-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 20px;
    margin-top: 20px;
    border: 2px dashed #ddd;
    border-radius: 4px;
    background: transparent;
    color: #888;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
  }

  .add-idea-btn:hover {
    border-color: #E59B3C;
    color: #E59B3C;
  }

  /* Practical Topics Section */
  .practical-section {
    background-color: #E59B3C;
    padding: 40px 50px;
    margin: 40px -50px 0 -50px;
    position: relative;
  }

  .practical-section .practical-title {
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -1px;
    text-transform: uppercase;
    color: #1a1a1a;
    margin-bottom: 20px;
  }

  .practical-section .practical-content {
    color: #1a1a1a;
    font-size: 14px;
    line-height: 1.7;
  }

  .practical-section .practical-content strong,
  .practical-section .practical-content b {
    font-weight: 800;
  }

  .practical-section .practical-title.editable:hover,
  .practical-section .practical-content.editable:hover {
    background: rgba(255,255,255,0.1);
  }

  .practical-section .practical-title.editable:focus,
  .practical-section .practical-content.editable:focus {
    background: rgba(255,255,255,0.2);
  }

  .practical-section:hover .format-toolbar {
    opacity: 1;
  }

  .practical-section:focus-within .format-toolbar {
    opacity: 1;
  }

  /* Preview Mode */
  .preview-mode .editable {
    border: none !important;
    cursor: default !important;
  }

  .preview-mode .editable:hover {
    border: none !important;
    background: transparent !important;
  }

  .preview-mode .format-toolbar,
  .preview-mode .criteria-toolbar,
  .preview-mode .idea-toolbar,
  .preview-mode .criteria-drag-handle,
  .preview-mode .criteria-delete,
  .preview-mode .idea-drag-handle,
  .preview-mode .idea-delete,
  .preview-mode .add-criteria-btn,
  .preview-mode .add-idea-btn {
    display: none !important;
  }

  /* Footer */
  .report-footer {
    margin-top: 60px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
    text-align: center;
  }

  .report-footer .disclaimer {
    font-size: 9px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  .report-footer .copyright {
    font-size: 9px;
    color: #666;
  }

  /* Editable elements */
  .editable {
    cursor: text;
    border: 1px dashed transparent;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 3px;
    transition: all 0.2s;
    min-height: 1em;
  }

  .editable:hover {
    border-color: #ccc;
    background: rgba(0,123,255,0.03);
  }

  .editable:focus {
    outline: none;
    border-color: #007bff;
    background: white;
  }

  .editable:empty:before {
    content: attr(data-placeholder);
    color: #aaa;
    font-style: italic;
  }

  /* Markdown preview */
  .markdown-preview {
    font-family: 'SF Mono', Monaco, Consolas, monospace;
    font-size: 13px;
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
    background: #f8f9fa;
    padding: 20px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    max-height: calc(100vh - 200px);
    overflow: auto;
    user-select: text;
  }

  /* Citation System Styles */
  .citation-ref {
    background: white;
    color: #E59B3C;
    padding: 0 2px;
    font-size: 1.05em;
    cursor: help;
    font-weight: bold;
    display: inline;
    vertical-align: baseline;
  }

  .citation-ref:hover {
    color: #d18a2f;
  }

  .citation-tooltip {
    position: fixed;
    background: white;
    color: #333;
    padding: 16px;
    border-radius: 8px;
    max-width: 400px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15), 0 8px 40px rgba(0,0,0,0.1);
    border: 1px solid #e0e0e0;
    z-index: 3000;
    font-size: 13px;
    line-height: 1.6;
    animation: tooltipFadeIn 0.15s ease-out;
  }

  @keyframes tooltipFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .citation-tooltip .tooltip-header {
    font-size: 0.75em;
    color: #E59B3C;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .citation-tooltip .tooltip-content {
    margin: 0;
    word-break: break-word;
    color: #333;
  }

  .citation-tooltip .tooltip-image {
    max-width: 100%;
    border-radius: 4px;
    margin-top: 8px;
  }

  .citation-tooltip .tooltip-delete {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
    text-align: right;
  }

  .citation-tooltip .tooltip-delete button {
    background: none;
    border: none;
    color: #dc3545;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 500;
  }

  .citation-tooltip .tooltip-delete button:hover {
    background: #fff5f5;
    color: #c82333;
  }

  .citation-toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #1a1a2e;
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 4000;
    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
    animation: toastSlideUp 0.2s ease-out;
  }

  @keyframes toastSlideUp {
    from { opacity: 0; transform: translate(-50%, 10px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }

  /* Citation Panel - floating bar at top */
  .citation-panel {
    position: fixed;
    top: 50px;
    left: 0;
    right: 0;
    z-index: 1500;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-bottom: 3px solid #E59B3C;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    animation: panelSlideDown 0.2s ease-out;
  }

  @keyframes panelSlideDown {
    from { opacity: 0; transform: translateY(-100%); }
    to { opacity: 1; transform: translateY(0); }
  }

  .citation-panel-content {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    gap: 20px;
  }

  .citation-panel-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
    max-width: 35%;
  }

  .citation-panel-label {
    background: #E59B3C;
    color: white;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }

  .citation-panel-text {
    color: #aaa;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .citation-panel-center {
    flex: 1;
    min-width: 0;
  }

  .citation-panel-source {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4ade80;
    font-size: 13px;
  }

  .citation-panel-source .source-label {
    color: #888;
    font-size: 11px;
    text-transform: uppercase;
  }

  .citation-panel-source .source-text {
    color: #ddd;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .citation-panel-hint {
    color: #E59B3C;
    font-size: 13px;
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  .citation-panel-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .citation-panel-right button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    border: none;
  }

  .citation-panel-right .btn-cancel {
    background: rgba(255,255,255,0.1);
    color: #aaa;
    padding: 8px 10px;
  }

  .citation-panel-right .btn-cancel:hover {
    background: rgba(255,255,255,0.2);
    color: white;
  }

  .citation-panel-right .btn-confirm {
    background: #E59B3C;
    color: white;
  }

  .citation-panel-right .btn-confirm:hover {
    background: #d18a2f;
  }

  .citation-panel-right .btn-confirm:disabled {
    background: #555;
    color: #888;
    cursor: not-allowed;
  }
`;

const DEFAULT_CRITERIA: CriteriaItem = {
  id: '',
  title: 'La <span class="orange">versatilidad (+)</span> y la capacidad de transformación del espacio',
  subtitle: 'VALOR',
  text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
};

const AdminReportEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setHeaderVisible } = useHeaderVisibility();

  // Vista simplificada: mantener la cabecera global visible
  useEffect(() => {
    setHeaderVisible(true);
  }, [setHeaderVisible]);

  const [report, setReport] = useState<EditableReport | null>(null);
  const [content, setContent] = useState<EditableReportContent | null>(null);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [generatingMarkdown, setGeneratingMarkdown] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [uploadingByGroup, setUploadingByGroup] = useState<Record<string, boolean>>({});
  const [downloadingHtmlByGroup, setDownloadingHtmlByGroup] = useState<Record<string, boolean>>({});
  const [previewingHtmlByGroup, setPreviewingHtmlByGroup] = useState<Record<string, boolean>>({});
  // Per-group AI markdown state
  const [generatingMdByGroup, setGeneratingMdByGroup] = useState<Record<string, boolean>>({});
  const [downloadingMdByGroup, setDownloadingMdByGroup] = useState<Record<string, boolean>>({});
  const [previewingMdByGroup, setPreviewingMdByGroup] = useState<Record<string, boolean>>({});
  const [markdownAvailableByGroup, setMarkdownAvailableByGroup] = useState<Record<string, boolean>>({});
  const groupPollingRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const finalPdfInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [leftPanelTab, setLeftPanelTab] = useState<'markdown' | 'html'>('markdown');
  const [criteriaItems, setCriteriaItems] = useState<CriteriaItem[]>([
    { ...DEFAULT_CRITERIA, id: `criteria-${Date.now()}` }
  ]);
  const [ideaItems, setIdeaItems] = useState<IdeaItem[]>([]);
  const [practicalTitle, setPracticalTitle] = useState<string>('TEMAS PRÁCTICOS');
  const [practicalContent, setPracticalContent] = useState<string>('Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.');
  const [previewMode, setPreviewMode] = useState<boolean>(false);

  // Citation system state
  const [citations, setCitations] = useState<Citation[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<{ citation: Citation; x: number; y: number } | null>(null);
  const [citationToast, setCitationToast] = useState<string | null>(null);
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [citationSourceText, setCitationSourceText] = useState('');
  const [citationSourceType, setCitationSourceType] = useState<'text' | 'image'>('text');
  const [citationCriteriaId, setCitationCriteriaId] = useState<string | null>(null);
  const [cursorCharOffset, setCursorCharOffset] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log('🔄 AdminReportEditor mounted, id:', id);
    if (id) loadReport(id);
  }, [id]);


  // Listen for messages from iframe (for selecting source content)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!showCitationModal) return;

      if (event.data.type === 'TEXT_SELECTED') {
        setCitationSourceText(event.data.text);
        setCitationSourceType('text');
      }
      if (event.data.type === 'IMAGE_CLICKED') {
        setCitationSourceText(event.data.src);
        setCitationSourceType('image');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showCitationModal]);


  useEffect(() => {
    if (hasChanges && content && id) {
      autoSaveTimeout.current = setTimeout(() => handleSave(true), 15000);
    }
    return () => { if (autoSaveTimeout.current) clearTimeout(autoSaveTimeout.current); };
  }, [hasChanges, content, id]);

  const loadReport = async (reportId: string) => {
    try {
      setLoading(true);
      const data = await getReport(reportId);
      console.log('📋 Report data loaded:', {
        hasMarkdown: !!data.markdown,
        markdownLength: data.markdown?.length,
        hasOriginalHtml: !!data.originalHtml,
        originalHtmlLength: data.originalHtml?.length,
        hasCitations: !!data.content?.citations,
        citationsCount: data.content?.citations?.length || 0,
        data
      });
      setReport(data);
      setContent(data.content || createEmptyContent(data));
      setMarkdown(data.markdown || '');
      // Seed the per-group markdown availability map from the serialized report
      const seedAvail: Record<string, boolean> = {};
      (data.groups || []).forEach((g) => {
        seedAvail[g.projectGroupId] = g.markdownAvailable === true;
      });
      setMarkdownAvailableByGroup(seedAvail);
      // Load existing citations
      if (data.content?.citations) {
        setCitations(data.content.citations);
      }
      // Load existing criteria items
      if (data.content?.criteriaItems && data.content.criteriaItems.length > 0) {
        setCriteriaItems(data.content.criteriaItems);
      }
      // Load existing idea items
      if (data.content?.ideaItems && data.content.ideaItems.length > 0) {
        setIdeaItems(data.content.ideaItems);
      }
      // Load practical section
      if (data.content?.practicalTitle) {
        setPracticalTitle(data.content.practicalTitle);
      }
      if (data.content?.practicalContent) {
        setPracticalContent(data.content.practicalContent);
      }
    } catch (err: any) {
      setError(err.message || 'Error cargando informe');
    } finally {
      setLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (reportId: string) => {
    stopPolling();
    setGeneratingMarkdown(true);
    pollingRef.current = setInterval(async () => {
      try {
        const updated = await getReport(reportId);
        if (updated.status === 'draft' || updated.status === 'published') {
          stopPolling();
          setReport(updated);
          setMarkdown(updated.markdown || '');
          setGeneratingMarkdown(false);
        } else if (updated.status === 'failed') {
          stopPolling();
          setReport(updated);
          setError(updated.generationError || 'Error generando el informe');
          setGeneratingMarkdown(false);
        }
      } catch {
        // Polling fetch failed — keep trying, don't abort
      }
    }, 3000);
  };

  const stopGroupPolling = (groupId: string) => {
    const handle = groupPollingRefs.current[groupId];
    if (handle) {
      clearInterval(handle);
      delete groupPollingRefs.current[groupId];
    }
  };

  /**
   * Start polling the per-group markdown endpoint. Stops when the `generatedAt`
   * timestamp differs from the one we captured before dispatching, which works
   * for both first generation (previous = null) and regeneration (previous = old timestamp).
   */
  const startGroupPolling = (reportId: string, groupId: string, previousGeneratedAt: string | null) => {
    stopGroupPolling(groupId);
    setGeneratingMdByGroup((prev) => ({ ...prev, [groupId]: true }));
    groupPollingRefs.current[groupId] = setInterval(async () => {
      try {
        const { available, generatedAt } = await getGroupMarkdown(reportId, groupId);
        if (available && generatedAt !== previousGeneratedAt) {
          stopGroupPolling(groupId);
          setGeneratingMdByGroup((prev) => ({ ...prev, [groupId]: false }));
          setMarkdownAvailableByGroup((prev) => ({ ...prev, [groupId]: true }));
        }
      } catch {
        // Polling fetch failed — keep trying, don't abort
      }
    }, 3000);
  };

  const handleGenerateGroupAi = async (groupId: string) => {
    if (!report) return;
    setError(null);
    let previousGeneratedAt: string | null = null;
    if (markdownAvailableByGroup[groupId]) {
      try {
        const cur = await getGroupMarkdown(report.id, groupId);
        previousGeneratedAt = cur.generatedAt;
      } catch {
        // best-effort; if the snapshot fails, polling stops as soon as
        // any markdown is available, which is still acceptable.
      }
    }
    try {
      await generateAiForGroup(report.id, groupId);
      startGroupPolling(report.id, groupId, previousGeneratedAt);
    } catch (err: any) {
      setError(err.message || 'Error generando el resultado IA del grupo');
      setGeneratingMdByGroup((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  const handleDownloadGroupMarkdown = async (groupId: string, groupName: string) => {
    if (!report) return;
    setDownloadingMdByGroup((prev) => ({ ...prev, [groupId]: true }));
    setError(null);
    try {
      const { markdown: md } = await getGroupMarkdown(report.id, groupId);
      if (!md) {
        setError('Este grupo aún no tiene resultado IA generado.');
        return;
      }
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeProject = (report.projectName || 'proyecto').replace(/[\\/:*?"<>|]+/g, '-');
      const safeGroup = groupName.replace(/[\\/:*?"<>|]+/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeProject} - ${safeGroup} - valoracion-ia.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'No se pudo descargar el markdown del grupo');
    } finally {
      setDownloadingMdByGroup((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  // Stop all per-group pollings when the editor unmounts
  useEffect(() => {
    return () => {
      Object.keys(groupPollingRefs.current).forEach((gid) => {
        clearInterval(groupPollingRefs.current[gid]);
      });
      groupPollingRefs.current = {};
    };
  }, []);

  // If report loads as 'generating', auto-start polling
  useEffect(() => {
    if (report?.status === 'generating') {
      startPolling(report.id);
    }
    return () => stopPolling();
  }, [report?.id, report?.status === 'generating']);

  const handleGenerateMarkdown = async () => {
    if (!report) return;
    try {
      setGeneratingMarkdown(true);
      setError(null);
      const updated = await generateAi(report.id);
      setReport(updated);
      startPolling(updated.id);
    } catch (err: any) {
      setError(err.message || 'Error generando el resultado de la IA');
      setGeneratingMarkdown(false);
    }
  };

  const handleDownloadGroupHtml = async (groupId: string, groupName: string) => {
    if (!report) return;
    setDownloadingHtmlByGroup(prev => ({ ...prev, [groupId]: true }));
    setError(null);
    try {
      const html = await getGroupPreviewHtml(report.id, groupId);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeProject = (report.projectName || 'proyecto').replace(/[\\/:*?"<>|]+/g, '-');
      const safeGroup = groupName.replace(/[\\/:*?"<>|]+/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeProject} - ${safeGroup} - resultados.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'No se pudo descargar el HTML del grupo');
    } finally {
      setDownloadingHtmlByGroup(prev => ({ ...prev, [groupId]: false }));
    }
  };

  const handlePreviewGroupHtml = async (groupId: string) => {
    if (!report) return;
    // Open the popup synchronously — browsers block window.open() called from
    // inside an async callback if it's not preceded by a user gesture.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setError('El navegador bloqueó la apertura de la pestaña. Permite popups para esta página.');
      return;
    }
    popup.document.title = 'Cargando…';
    popup.document.body.innerHTML = '<p style="font-family:system-ui;padding:2rem;color:#666;">Cargando…</p>';
    setPreviewingHtmlByGroup(prev => ({ ...prev, [groupId]: true }));
    setError(null);
    try {
      const html = await getGroupPreviewHtml(report.id, groupId);
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
    } catch (err: any) {
      popup.close();
      setError(err.message || 'No se pudo abrir la previsualización');
    } finally {
      setPreviewingHtmlByGroup(prev => ({ ...prev, [groupId]: false }));
    }
  };

  const handlePreviewGroupMarkdown = async (groupId: string, groupName: string) => {
    if (!report) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setError('El navegador bloqueó la apertura de la pestaña. Permite popups para esta página.');
      return;
    }
    popup.document.title = 'Cargando…';
    popup.document.body.innerHTML = '<p style="font-family:system-ui;padding:2rem;color:#666;">Cargando…</p>';
    setPreviewingMdByGroup(prev => ({ ...prev, [groupId]: true }));
    setError(null);
    try {
      const { markdown: md } = await getGroupMarkdown(report.id, groupId);
      if (!md) {
        popup.close();
        setError('Este grupo aún no tiene resultado IA generado.');
        return;
      }
      const body = await marked.parse(md, { async: true });
      const docTitle = `${report.projectName} — ${groupName}`;
      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
         .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escapeHtml(docTitle)}</title>
<style>
  :root { color-scheme: light; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif;
    color: #1a1a1a;
    background: #fff;
    max-width: 820px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.65;
    font-size: 16px;
  }
  h1, h2, h3, h4, h5 { font-weight: 600; line-height: 1.25; margin-top: 2.2rem; }
  h1 { font-size: 1.9rem; border-bottom: 1px solid #eee; padding-bottom: .5rem; }
  h2 { font-size: 1.45rem; }
  h3 { font-size: 1.2rem; }
  p { margin: 1rem 0; }
  ul, ol { padding-left: 1.4rem; }
  li { margin: .35rem 0; }
  blockquote { border-left: 4px solid #ddd; padding-left: 1rem; color: #555; margin: 1rem 0; }
  code { background: #f4f4f4; padding: .15rem .4rem; border-radius: 3px; font-size: .9em; }
  pre { background: #f4f4f4; padding: 1rem; border-radius: 6px; overflow-x: auto; }
  pre code { background: transparent; padding: 0; }
  u { text-decoration-color: #f7aa28; text-decoration-thickness: 2px; }
  hr { border: none; border-top: 1px solid #eee; margin: 2rem 0; }
  .doc-meta { color: #999; font-size: .85rem; margin-bottom: 2rem; }
</style>
</head>
<body>
  <div class="doc-meta">${escapeHtml(report.projectName)} · ${escapeHtml(groupName)}</div>
  ${body}
</body>
</html>`;
      popup.document.open();
      popup.document.write(fullHtml);
      popup.document.close();
    } catch (err: any) {
      popup.close();
      setError(err.message || 'No se pudo abrir la previsualización');
    } finally {
      setPreviewingMdByGroup(prev => ({ ...prev, [groupId]: false }));
    }
  };

  const validatePdfFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'El fichero debe ser un PDF.';
    }
    if (file.size > 20 * 1024 * 1024) {
      return 'El PDF no puede superar los 20 MB.';
    }
    return null;
  };

  const handleUploadForGroup = async (
    groupId: string,
    alreadyHasPdf: boolean,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    // Reset so selecting the same file again re-triggers onChange
    e.target.value = '';
    if (!file || !report) return;

    const validationError = validatePdfFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (alreadyHasPdf && !window.confirm('¿Reemplazar el informe final actual?')) {
      return;
    }

    try {
      setUploadingByGroup((prev) => ({ ...prev, [groupId]: true }));
      setError(null);
      const updated = await uploadFinalPdf(report.id, groupId, file);
      setReport(updated);
    } catch (err: any) {
      setError(err.message || 'Error subiendo el informe final');
    } finally {
      setUploadingByGroup((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    }
  };

  const handleDeleteForGroup = async (groupId: string) => {
    if (!report) return;
    if (!window.confirm('¿Eliminar el informe final?')) return;

    try {
      setUploadingByGroup((prev) => ({ ...prev, [groupId]: true }));
      setError(null);
      const updated = await deleteFinalPdf(report.id, groupId);
      setReport(updated);
    } catch (err: any) {
      setError(err.message || 'Error eliminando el informe final');
    } finally {
      setUploadingByGroup((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    }
  };

  const createEmptyContent = (report: EditableReport): EditableReportContent => ({
    id: report.id,
    projectId: report.projectId,
    version: 1,
    status: 'draft',
    metadata: report.metadata,
    header: {
      title: 'ESTUDIO DE PERCEPCIÓN ESPACIAL',
      familyName: report.metadata?.familyName || '',
      subtitle: report.projectName || '',
    },
    sections: [],
    footer: {
      disclaimer: 'El presente informe se emite con carácter estrictamente informativo y como herramienta de apoyo para el desarrollo del proyecto arquitectónico. En ningún caso el contenido de este estudio constituye, ni pretende sustituir, un diagnóstico clínico, facultativo o psicológico. Los resultados aquí expuestos derivan de una dinámica experiencial de percepción subjetiva y carecen de validez médica o terapéutica.',
      generatedDate: new Date().toISOString(),
      copyright: '© Todos los derechos reservados a PLANO INVISIBLE.',
    },
  });

  const handleSave = async (isAutoSave = false) => {
    if (!content || !id) return;
    try {
      setSaving(true);
      // Include citations, criteria items, idea items, and practical section in the content
      const contentToSave = {
        ...content,
        citations,
        criteriaItems,
        ideaItems,
        practicalTitle,
        practicalContent,
      };
      await updateReport(id, contentToSave);
      setHasChanges(false);
      if (!isAutoSave) {
        setSuccess('Guardado');
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!id) return;
    try {
      setPublishing(true);
      if (hasChanges && content) {
        await updateReport(id, content);
        setHasChanges(false);
      }
      const result = await publishReport(id);
      setReport(result.report);
      setSuccess('Publicado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Error publicando');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    try {
      setPublishing(true);
      const updated = await unpublishReport(id);
      setReport(updated);
      setSuccess('Despublicado');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.message || 'Error');
    } finally {
      setPublishing(false);
    }
  };

  const updateHeader = (field: string, value: string) => {
    if (!content) return;
    setContent(prev => prev ? { ...prev, header: { ...prev.header, [field]: value } } : prev);
    setHasChanges(true);
  };

  // Criteria functions
  const addCriteria = () => {
    setCriteriaItems([...criteriaItems, { ...DEFAULT_CRITERIA, id: `criteria-${Date.now()}` }]);
    setHasChanges(true);
  };

  const removeCriteria = (id: string) => {
    const newItems = criteriaItems.filter(c => c.id !== id);

    // Sync citations after removing criteria
    const { updatedItems, updatedCitations } = syncCitationsAll(newItems, ideaItems, citations);

    setCriteriaItems(updatedItems.criteria);
    setIdeaItems(updatedItems.ideas);
    setCitations(updatedCitations);
    setHasChanges(true);
  };

  const updateCriteria = (id: string, field: keyof CriteriaItem, value: string) => {
    const newItems = criteriaItems.map(c => c.id === id ? { ...c, [field]: value } : c);

    // Sync citations (remove unused, renumber)
    const { updatedItems, updatedCitations } = syncCitationsAll(newItems, ideaItems, citations);

    setCriteriaItems(updatedItems.criteria);
    setIdeaItems(updatedItems.ideas);
    if (updatedCitations.length !== citations.length ||
        updatedCitations.some((c, i) => c.id !== citations[i]?.id)) {
      setCitations(updatedCitations);
    }
    setHasChanges(true);
  };

  // Idea functions
  const addIdea = () => {
    setIdeaItems([...ideaItems, {
      id: `idea-${Date.now()}`,
      title: 'Nueva idea',
      text: 'Descripción de la idea...'
    }]);
    setHasChanges(true);
  };

  const removeIdea = (id: string) => {
    const newItems = ideaItems.filter(i => i.id !== id);

    // Sync citations after removing idea
    const { updatedItems, updatedCitations } = syncCitationsAll(criteriaItems, newItems, citations);

    setCriteriaItems(updatedItems.criteria);
    setIdeaItems(updatedItems.ideas);
    setCitations(updatedCitations);
    setHasChanges(true);
  };

  const updateIdea = (id: string, field: keyof IdeaItem, value: string) => {
    const newItems = ideaItems.map(i => i.id === id ? { ...i, [field]: value } : i);

    // Sync citations (remove unused, renumber)
    const { updatedItems, updatedCitations } = syncCitationsAll(criteriaItems, newItems, citations);

    setCriteriaItems(updatedItems.criteria);
    setIdeaItems(updatedItems.ideas);
    if (updatedCitations.length !== citations.length ||
        updatedCitations.some((c, i) => c.id !== citations[i]?.id)) {
      setCitations(updatedCitations);
    }
    setHasChanges(true);
  };

  const applyColorToSelection = (color: 'orange' | 'black') => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    const span = document.createElement('span');
    if (color === 'orange') {
      span.className = 'orange';
    } else {
      span.style.color = '#1a1a1a';
    }

    try {
      range.surroundContents(span);
      setHasChanges(true);
    } catch (e) {
      // If selection spans multiple elements, use execCommand as fallback
      document.execCommand('foreColor', false, color === 'orange' ? '#E59B3C' : '#1a1a1a');
      setHasChanges(true);
    }
  };

  // Handle paste to strip background/color styles but keep bold/italic
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    if (html) {
      // Parse HTML and remove unwanted styles (background, color, font-family, font-size)
      const cleanHtml = html
        .replace(/style="[^"]*"/gi, (match) => {
          // Keep only font-weight and font-style
          const fontWeight = match.match(/font-weight\s*:\s*[^;"]*/i);
          const fontStyle = match.match(/font-style\s*:\s*[^;"]*/i);
          const keepStyles = [fontWeight?.[0], fontStyle?.[0]].filter(Boolean).join(';');
          return keepStyles ? `style="${keepStyles}"` : '';
        })
        .replace(/<span[^>]*>|<\/span>/gi, '') // Remove span tags
        .replace(/<font[^>]*>|<\/font>/gi, ''); // Remove font tags

      document.execCommand('insertHTML', false, cleanHtml);
    } else {
      document.execCommand('insertText', false, text);
    }
    setHasChanges(true);
  };

  const onCriteriaDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(criteriaItems);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setCriteriaItems(items);
    setHasChanges(true);
  };

  const onIdeaDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(ideaItems);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setIdeaItems(items);
    setHasChanges(true);
  };

  // Citation functions
  const getNextCitationId = () => {
    if (citations.length === 0) return 1;
    return Math.max(...citations.map(c => c.id)) + 1;
  };

  // Calculate character offset of cursor within an element's text content
  const getCaretCharOffset = (element: HTMLElement): number => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return -1;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    return preCaretRange.toString().length;
  };

  // Insert text at a character offset within HTML content
  const insertAtCharOffset = (html: string, offset: number, textToInsert: string): string => {
    // Create a temporary div to work with the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    let currentOffset = 0;
    let inserted = false;

    const walkNodes = (node: Node): boolean => {
      if (inserted) return true;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length || 0;
        if (currentOffset + textLength >= offset) {
          // Insert here
          const insertAt = offset - currentOffset;
          const text = node.textContent || '';
          node.textContent = text.slice(0, insertAt) + textToInsert + text.slice(insertAt);
          inserted = true;
          return true;
        }
        currentOffset += textLength;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        for (const child of Array.from(node.childNodes)) {
          if (walkNodes(child)) return true;
        }
      }
      return false;
    };

    walkNodes(temp);

    // If we couldn't insert (offset beyond text), append at end
    if (!inserted) {
      temp.innerHTML = html + textToInsert;
    }

    return temp.innerHTML;
  };

  // Search for text in the original HTML iframe
  const searchInOriginalHtml = (searchText: string): string | null => {
    if (!iframeRef.current || !searchText || searchText.length < 10) return null;

    try {
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (!iframeDoc) return null;

      // Clean the search text (remove quotes, extra spaces)
      const cleanSearch = searchText
        .replace(/^[""]|[""]$/g, '') // Remove surrounding quotes
        .replace(/\s+/g, ' ')
        .trim();

      if (cleanSearch.length < 10) return null;

      // Get all text content from the iframe
      const bodyText = iframeDoc.body.innerText || '';

      // Try to find the text (case insensitive, allowing for some flexibility)
      const searchLower = cleanSearch.toLowerCase();
      const bodyLower = bodyText.toLowerCase();

      // First try exact match
      if (bodyLower.includes(searchLower)) {
        // Find the actual text with original casing
        const startIdx = bodyLower.indexOf(searchLower);
        const foundText = bodyText.substring(startIdx, startIdx + cleanSearch.length);
        return foundText;
      }

      // Try matching first 50 characters if exact match fails
      const shortSearch = searchLower.substring(0, 50);
      if (shortSearch.length >= 10 && bodyLower.includes(shortSearch)) {
        const startIdx = bodyLower.indexOf(shortSearch);
        // Get a bit more context (up to 200 chars)
        const endIdx = Math.min(startIdx + 200, bodyText.length);
        const foundText = bodyText.substring(startIdx, endIdx);
        // Trim to sentence or reasonable boundary
        const trimmed = foundText.replace(/[.!?].*$/, '.') || foundText;
        return trimmed;
      }

      return null;
    } catch (e) {
      console.error('Error searching in iframe:', e);
      return null;
    }
  };

  // Extract citation text from selection (look for underlined/bold text)
  const extractCitationText = (): string | null => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      // No selection, try to find underlined text near cursor
      const activeEl = document.activeElement as HTMLElement;
      if (activeEl?.closest('.editable')) {
        // Look for <u> tags in the editable content
        const underlined = activeEl.querySelector('u');
        if (underlined) {
          return underlined.textContent?.trim() || null;
        }
      }
      return null;
    }

    // Get selected text
    const selectedText = selection.toString().trim();
    if (selectedText.length >= 10) {
      return selectedText;
    }

    // If selection is too short, check if it's inside an underlined element
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const parentEl = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement;

    const underlined = parentEl?.closest('u') || parentEl?.querySelector('u');
    if (underlined) {
      return underlined.textContent?.trim() || null;
    }

    return selectedText.length > 0 ? selectedText : null;
  };

  // Open modal to select source from original HTML
  const handleOpenCitationModal = (itemId: string) => {
    // Save the item ID we're working with (criteria or idea)
    setCitationCriteriaId(itemId);

    // Determine if it's a criteria or idea
    const isCriteria = itemId.startsWith('criteria-');
    const isIdea = itemId.startsWith('idea-');

    let textEl: HTMLElement | null = null;

    if (isCriteria) {
      // Find the criteria module and text element
      const criteriaModule = document.querySelector(`[data-criteria-id="${itemId}"]`);
      textEl = criteriaModule?.querySelector('.criteria-right') as HTMLElement;

      // Capture cursor position as character offset FIRST
      if (textEl) {
        const offset = getCaretCharOffset(textEl);
        setCursorCharOffset(offset >= 0 ? offset : null);
      } else {
        setCursorCharOffset(null);
      }

      // Save current content of all editable fields before opening modal
      if (criteriaModule) {
        const titleEl = criteriaModule.querySelector('.criteria-title') as HTMLElement;
        const subtitleEl = criteriaModule.querySelector('.criteria-subtitle') as HTMLElement;

        if (titleEl || subtitleEl || textEl) {
          setCriteriaItems(prev => prev.map(c => {
            if (c.id === itemId) {
              return {
                ...c,
                title: stripCitationSpans(titleEl?.innerHTML || c.title),
                subtitle: subtitleEl?.textContent || c.subtitle,
                text: stripCitationSpans(textEl?.innerHTML || c.text),
              };
            }
            return c;
          }));
        }
      }
    } else if (isIdea) {
      // Find the idea module and text element
      const ideaModule = document.querySelector(`[data-idea-id="${itemId}"]`);
      textEl = ideaModule?.querySelector('.idea-text') as HTMLElement;

      // Capture cursor position as character offset FIRST
      if (textEl) {
        const offset = getCaretCharOffset(textEl);
        setCursorCharOffset(offset >= 0 ? offset : null);
      } else {
        setCursorCharOffset(null);
      }

      // Save current content of all editable fields before opening modal
      if (ideaModule) {
        const titleEl = ideaModule.querySelector('.idea-title') as HTMLElement;

        if (titleEl || textEl) {
          setIdeaItems(prev => prev.map(i => {
            if (i.id === itemId) {
              return {
                ...i,
                title: stripCitationSpans(titleEl?.innerHTML || i.title),
                text: stripCitationSpans(textEl?.innerHTML || i.text),
              };
            }
            return i;
          }));
        }
      }
    }

    // Try to auto-find the citation source from selected/underlined text
    const citationText = extractCitationText();
    if (citationText) {
      const foundInOriginal = searchInOriginalHtml(citationText);
      if (foundInOriginal) {
        setCitationSourceText(foundInOriginal);
        setCitationSourceType('text');
        setCitationToast('Fuente encontrada automáticamente');
        setTimeout(() => setCitationToast(null), 2000);
      } else {
        setCitationSourceText('');
      }
    } else {
      setCitationSourceText('');
    }

    setShowCitationModal(true);
  };

  // Confirm citation with selected source
  const handleConfirmCitation = () => {
    if (!citationSourceText) {
      setCitationToast('Selecciona texto o imagen del HTML original');
      setTimeout(() => setCitationToast(null), 2500);
      return;
    }

    if (!citationCriteriaId) {
      setCitationToast('Error: no se encontró el elemento');
      setTimeout(() => setCitationToast(null), 2500);
      return;
    }

    const newId = getNextCitationId();
    const newCitation: Citation = {
      id: newId,
      type: citationSourceType,
      content: citationSourceText.substring(0, 500),
    };
    setCitations(prev => [...prev, newCitation]);

    // Insert [n] into the text at cursor position
    const marker = `[${newId}]`;

    // Check if it's a criteria or idea
    if (citationCriteriaId.startsWith('criteria-')) {
      setCriteriaItems(prev => prev.map(c => {
        if (c.id === citationCriteriaId) {
          let newText: string;
          if (cursorCharOffset !== null && cursorCharOffset >= 0) {
            newText = insertAtCharOffset(c.text, cursorCharOffset, marker);
          } else {
            newText = c.text + ' ' + marker;
          }
          return { ...c, text: newText };
        }
        return c;
      }));
    } else if (citationCriteriaId.startsWith('idea-')) {
      setIdeaItems(prev => prev.map(i => {
        if (i.id === citationCriteriaId) {
          let newText: string;
          if (cursorCharOffset !== null && cursorCharOffset >= 0) {
            newText = insertAtCharOffset(i.text, cursorCharOffset, marker);
          } else {
            newText = i.text + ' ' + marker;
          }
          return { ...i, text: newText };
        }
        return i;
      }));
    }

    setHasChanges(true);
    setShowCitationModal(false);
    setCitationSourceText('');
    setCitationCriteriaId(null);
    setCursorCharOffset(null);

    setCitationToast(`Cita [${newId}] insertada`);
    setTimeout(() => setCitationToast(null), 2500);
  };

  const handleCancelCitation = () => {
    setShowCitationModal(false);
    setCitationSourceText('');
    setCitationCriteriaId(null);
    setCursorCharOffset(null);
  };

  // Process HTML to convert [n] into styled citation spans for display
  const processHtmlWithCitations = (html: string): string => {
    if (!html) return html;

    // First, strip any existing citation-ref spans (to avoid double-wrapping)
    let cleanHtml = html.replace(/<span class="citation-ref"[^>]*>(\[\d+\])<\/span>/g, '$1');

    // Then wrap all [n] patterns with styled spans
    return cleanHtml.replace(/\[(\d+)\]/g, (match, num) => {
      const citationId = parseInt(num);
      const citation = citations.find(c => c.id === citationId);
      if (citation) {
        return `<span class="citation-ref" data-citation-id="${citationId}">[${citationId}]</span>`;
      }
      return match; // Keep as-is if citation not found
    });
  };

  // Strip citation spans from HTML before saving (keep only [n] text)
  const stripCitationSpans = (html: string): string => {
    return html.replace(/<span class="citation-ref"[^>]*>(\[\d+\])<\/span>/g, '$1');
  };

  // Sync citations: remove unused, renumber based on order of appearance (for both criteria and ideas)
  const syncCitationsAll = (
    criteriaItems: CriteriaItem[],
    ideaItems: IdeaItem[],
    currentCitations: Citation[]
  ): {
    updatedItems: { criteria: CriteriaItem[], ideas: IdeaItem[] },
    updatedCitations: Citation[]
  } => {
    // Collect all citation IDs in order of appearance across all criteria and ideas
    const allCriteriaText = criteriaItems.map(c => c.title + ' ' + c.text).join(' ');
    const allIdeasText = ideaItems.map(i => i.title + ' ' + i.text).join(' ');
    const allText = allCriteriaText + ' ' + allIdeasText;

    const citationMatches = allText.match(/\[(\d+)\]/g) || [];
    const usedIds = citationMatches.map(m => parseInt(m.replace(/[\[\]]/g, '')));
    const uniqueUsedIds = Array.from(new Set(usedIds));

    // Filter out unused citations
    const usedCitations = currentCitations.filter(c => uniqueUsedIds.includes(c.id));

    // Create mapping from old ID to new ID (based on order of first appearance)
    const idMapping: Record<number, number> = {};
    uniqueUsedIds.forEach((oldId, index) => {
      idMapping[oldId] = index + 1;
    });

    // Check if renumbering is needed
    const needsRenumbering = uniqueUsedIds.some((oldId, index) => oldId !== index + 1);

    if (!needsRenumbering) {
      // Just filter unused citations
      return {
        updatedItems: { criteria: criteriaItems, ideas: ideaItems },
        updatedCitations: usedCitations
      };
    }

    // Helper to renumber text
    const renumberText = (text: string) => text.replace(/\[(\d+)\]/g, (match, num) => {
      const oldId = parseInt(num);
      return idMapping[oldId] ? `[${idMapping[oldId]}]` : match;
    });

    // Renumber citations in criteria
    const updatedCriteria = criteriaItems.map(item => ({
      ...item,
      title: renumberText(item.title),
      text: renumberText(item.text),
    }));

    // Renumber citations in ideas
    const updatedIdeas = ideaItems.map(item => ({
      ...item,
      title: renumberText(item.title),
      text: renumberText(item.text),
    }));

    // Renumber citation objects
    const updatedCitations = usedCitations.map(c => ({
      ...c,
      id: idMapping[c.id] || c.id,
    })).sort((a, b) => a.id - b.id);

    return {
      updatedItems: { criteria: updatedCriteria, ideas: updatedIdeas },
      updatedCitations
    };
  };

  // Handle hover on citation refs to show tooltip
  const handleCitationHover = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('citation-ref')) {
      const citationId = parseInt(target.dataset.citationId || '0');
      const citation = citations.find(c => c.id === citationId);
      if (citation) {
        const rect = target.getBoundingClientRect();
        setActiveTooltip({
          citation,
          x: Math.min(rect.left, window.innerWidth - 420),
          y: rect.bottom + 8
        });
      }
    }
  };

  const handleCitationLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    // Only hide if we're not moving to another citation-ref or to the tooltip
    if (!relatedTarget?.classList?.contains('citation-ref') &&
        !relatedTarget?.closest('.citation-tooltip')) {
      setActiveTooltip(null);
    }
  };

  // Delete a citation by ID
  const handleDeleteCitation = (citationId: number) => {
    // Remove [n] from all criteria text
    const updatedCriteriaItems = criteriaItems.map(item => ({
      ...item,
      title: item.title.replace(new RegExp(`\\[${citationId}\\]`, 'g'), ''),
      text: item.text.replace(new RegExp(`\\[${citationId}\\]`, 'g'), ''),
    }));

    // Remove [n] from all ideas text
    const updatedIdeaItems = ideaItems.map(item => ({
      ...item,
      title: item.title.replace(new RegExp(`\\[${citationId}\\]`, 'g'), ''),
      text: item.text.replace(new RegExp(`\\[${citationId}\\]`, 'g'), ''),
    }));

    // Sync citations (this will also renumber)
    const { updatedItems, updatedCitations } = syncCitationsAll(updatedCriteriaItems, updatedIdeaItems, citations);

    setCriteriaItems(updatedItems.criteria);
    setIdeaItems(updatedItems.ideas);
    setCitations(updatedCitations);
    setActiveTooltip(null);
    setHasChanges(true);

    setCitationToast('Cita eliminada');
    setTimeout(() => setCitationToast(null), 2000);
  };

  const showTooltip = (citation: Citation, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    // Position tooltip below the citation ref, but keep within viewport
    const x = Math.min(rect.left, window.innerWidth - 420);
    const y = rect.bottom + 8;
    setActiveTooltip({ citation, x, y });
  };

  const hideTooltip = () => {
    setActiveTooltip(null);
  };

  // Render text with citation tooltips
  const renderTextWithCitations = (html: string): React.ReactNode[] => {
    // Split by citation markers like [1], [2], etc.
    const parts = html.split(/(\[\d+\])/g);
    return parts.map((part, i) => {
      const match = part.match(/^\[(\d+)\]$/);
      if (match) {
        const citationId = parseInt(match[1]);
        const citation = citations.find(c => c.id === citationId);
        if (citation) {
          return (
            <span
              key={i}
              className="citation-ref"
              onMouseEnter={(e) => showTooltip(citation, e)}
              onMouseLeave={hideTooltip}
            >
              [{citationId}]
            </span>
          );
        }
        // Citation not found, render as plain text
        return <span key={i}>[{citationId}]</span>;
      }
      // Regular HTML content
      return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
    });
  };

  // Script to inject into iframe for detecting selection and image clicks
  const getIframeInjectionScript = () => `
    <script>
      // Detect text selection
      document.addEventListener('mouseup', (e) => {
        setTimeout(() => {
          const selection = window.getSelection();
          const text = selection ? selection.toString().trim() : '';
          if (text.length > 5) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            // Find section title
            let sectionTitle = '';
            let el = range.startContainer.parentElement;
            while (el) {
              if (el.tagName === 'H2' || el.tagName === 'H3') {
                sectionTitle = el.textContent || '';
                break;
              }
              const h2 = el.querySelector('h2, h3');
              if (h2) {
                sectionTitle = h2.textContent || '';
                break;
              }
              el = el.parentElement;
            }
            window.parent.postMessage({
              type: 'TEXT_SELECTED',
              text: text,
              sectionTitle: sectionTitle,
              position: { x: rect.left + window.scrollX, y: rect.bottom + window.scrollY }
            }, '*');
          }
        }, 10);
      });

      // Detect image clicks
      document.querySelectorAll('img').forEach(img => {
        img.style.cursor = 'pointer';
        img.title = 'Clic para seleccionar como fuente de cita';
        img.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Find section title
          let sectionTitle = '';
          let el = img.parentElement;
          while (el) {
            const h2 = el.querySelector('h2, h3');
            if (h2) {
              sectionTitle = h2.textContent || '';
              break;
            }
            if (el.tagName === 'H2' || el.tagName === 'H3') {
              sectionTitle = el.textContent || '';
              break;
            }
            el = el.parentElement;
          }
          window.parent.postMessage({
            type: 'IMAGE_CLICKED',
            src: img.src,
            alt: img.alt,
            sectionTitle: sectionTitle
          }, '*');
        });
      });

      // Add visual feedback for selectable content
      const style = document.createElement('style');
      style.textContent = \`
        body {
          cursor: text;
        }
        img {
          transition: outline 0.15s, transform 0.15s;
          cursor: pointer !important;
        }
        img:hover {
          outline: 3px solid #E59B3C;
          outline-offset: 2px;
        }
        ::selection {
          background: #E59B3C;
          color: white;
        }
      \`;
      document.head.appendChild(style);
    </script>
  `;

  // Inject script into original HTML
  const getEnhancedHtml = (html: string) => {
    if (!html) return html;
    // Insert script before closing body tag
    if (html.includes('</body>')) {
      return html.replace('</body>', getIframeInjectionScript() + '</body>');
    }
    // If no body tag, append at the end
    return html + getIframeInjectionScript();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error || 'No se pudo cargar'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/projects')}>Volver</button>
      </div>
    );
  }

  // --- Editor del constructor deshabilitado: vista simplificada con descargas ---
  // El JSX del editor original (drag-and-drop, citas, previsualizaciones) se
  // ha eliminado del render; está disponible en el historial de git si hace
  // falta recuperarlo. Los estados, efectos y handlers del editor se mantienen
  // declarados arriba para no romper los hooks ni la carga de datos.
  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="bi bi-file-earmark-text me-2"></i>
          {report.projectName}
          {report.groupName && (
            <span className="text-muted fs-5 ms-2">· {report.groupName}</span>
          )}
        </h2>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${report.status === 'published' ? 'bg-success' : 'bg-warning text-dark'}`}>
            {report.status === 'published' ? 'Publicado' : 'Borrador'}
          </span>
          <button className="btn btn-outline-secondary btn-sm text-nowrap" onClick={() => navigate('/admin/projects')}>
            <i className="bi bi-arrow-left me-2"></i>
            Volver
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {report.groups && report.groups.length > 0 ? (
        <div className="d-flex flex-column gap-3">
          {report.groups.map((group) => {
            const isSubmitted = group.submitted === true;
            const isHtmlDownloading = !!downloadingHtmlByGroup[group.projectGroupId];
            const isMdAvailable = !!markdownAvailableByGroup[group.projectGroupId];
            const isMdGenerating = !!generatingMdByGroup[group.projectGroupId];
            const isMdDownloading = !!downloadingMdByGroup[group.projectGroupId];
            const isPdfUploading = !!uploadingByGroup[group.projectGroupId];
            const hasPdf = !!group.finalPdfUrl;
            return (
              <div key={group.projectGroupId} className="card">
                <div className="card-header d-flex justify-content-between align-items-center bg-light">
                  <div>
                    <i className="bi bi-folder me-2"></i>
                    <strong>{group.groupName}</strong>
                  </div>
                  <span
                    className={`badge ${isSubmitted ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'}`}
                    title={isSubmitted ? 'El cliente ha completado este envío' : 'El cliente todavía no ha completado este envío'}
                  >
                    <i className={`bi ${isSubmitted ? 'bi-check-circle' : 'bi-hourglass-split'} me-1`}></i>
                    {isSubmitted ? 'Completado' : 'Pendiente'}
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table table-borderless table-hover align-middle mb-0 group-document-table">
                    <tbody>
                      {/* HTML row */}
                      <tr>
                        <td>
                          <i className="bi bi-filetype-html me-2"></i>
                          Resultados del test (HTML)
                        </td>
                        <td className="text-end" style={{ width: '180px' }}>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-dark"
                            onClick={() => handlePreviewGroupHtml(group.projectGroupId)}
                            disabled={!!previewingHtmlByGroup[group.projectGroupId] || !isSubmitted}
                            title={!isSubmitted ? 'El cliente aún no ha completado este envío' : 'Ver en una pestaña nueva'}
                          >
                            {previewingHtmlByGroup[group.projectGroupId] ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <i className="bi bi-eye"></i>
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-dark ms-1"
                            onClick={() => handleDownloadGroupHtml(group.projectGroupId, group.groupName)}
                            disabled={isHtmlDownloading || !isSubmitted}
                            title={!isSubmitted ? 'El cliente aún no ha completado este envío' : 'Descargar HTML de este grupo'}
                          >
                            {isHtmlDownloading ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <i className="bi bi-download"></i>
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* AI markdown row */}
                      <tr>
                        <td>
                          <i className="bi bi-filetype-md me-2"></i>
                          Resultado IA (markdown)
                        </td>
                        <td className="text-end">
                          {isMdAvailable ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-dark"
                                onClick={() => handlePreviewGroupMarkdown(group.projectGroupId, group.groupName)}
                                disabled={!!previewingMdByGroup[group.projectGroupId] || isMdGenerating}
                                title="Ver en una pestaña nueva"
                              >
                                {previewingMdByGroup[group.projectGroupId] ? (
                                  <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                  <i className="bi bi-eye"></i>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-dark ms-1"
                                onClick={() => handleDownloadGroupMarkdown(group.projectGroupId, group.groupName)}
                                disabled={isMdDownloading || isMdGenerating}
                                title="Descargar markdown IA de este grupo"
                              >
                                {isMdDownloading ? (
                                  <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                  <i className="bi bi-download"></i>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-muted ms-1"
                                onClick={() => handleGenerateGroupAi(group.projectGroupId)}
                                disabled={isMdGenerating || !isSubmitted}
                                title={!isSubmitted ? 'El cliente aún no ha completado este envío' : 'Regenerar resultado IA con los prompts actuales del grupo'}
                              >
                                {isMdGenerating ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Regenerando...
                                  </>
                                ) : (
                                  <i className="bi bi-arrow-clockwise"></i>
                                )}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-muted"
                              onClick={() => handleGenerateGroupAi(group.projectGroupId)}
                              disabled={isMdGenerating || !isSubmitted}
                              title={!isSubmitted ? 'El cliente aún no ha completado este envío' : 'Generar resultado IA para este grupo'}
                            >
                              {isMdGenerating ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                  Generando...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-stars me-1"></i>
                                  Generar
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Final PDF row */}
                      <tr className="group-document-row-final">
                        <td>
                          <i className="bi bi-filetype-pdf me-2"></i>
                          <strong>Informe final (PDF)</strong>
                        </td>
                        <td className="text-end">
                          <input
                            ref={(el) => {
                              finalPdfInputRefs.current[group.projectGroupId] = el;
                            }}
                            type="file"
                            accept=".pdf,application/pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => handleUploadForGroup(group.projectGroupId, hasPdf, e)}
                          />
                          {isPdfUploading ? (
                            <span className="btn btn-link btn-sm text-muted disabled">
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              Procesando...
                            </span>
                          ) : hasPdf ? (
                            <>
                              <a
                                className="btn btn-link btn-sm text-dark"
                                href={group.finalPdfPreviewUrl || group.finalPdfUrl || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Ver en una pestaña nueva"
                              >
                                <i className="bi bi-eye"></i>
                              </a>
                              <a
                                className="btn btn-link btn-sm text-dark"
                                href={group.finalPdfUrl || undefined}
                                download={`${report.projectName}-${group.groupName}-informe-final.pdf`}
                                title="Descargar"
                              >
                                <i className="bi bi-download"></i>
                              </a>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-dark"
                                onClick={() => finalPdfInputRefs.current[group.projectGroupId]?.click()}
                                title="Reemplazar"
                              >
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-danger"
                                onClick={() => handleDeleteForGroup(group.projectGroupId)}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-muted"
                              onClick={() => finalPdfInputRefs.current[group.projectGroupId]?.click()}
                            >
                              <i className="bi bi-upload me-1"></i>
                              Subir
                            </button>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Legacy fallback (reports with no groups serialized)
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Documento</th>
                <th className="text-end">Descargar</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <i className="bi bi-filetype-html me-2"></i>
                  Resultados del test (HTML)
                </td>
                <td className="text-end">
                  {report.originalHtml ? (
                    <a
                      className="btn btn-link btn-sm text-dark"
                      href={`data:text/html;charset=utf-8,${encodeURIComponent(report.originalHtml)}`}
                      download={`${report.projectName}-resultados.html`}
                      title="Descargar"
                    >
                      <i className="bi bi-download"></i>
                    </a>
                  ) : (
                    <span className="text-muted small">No disponible</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <i className="bi bi-filetype-md me-2"></i>
                  Resultado IA (markdown)
                </td>
                <td className="text-end">
                  {markdown ? (
                    <a
                      className="btn btn-link btn-sm text-dark"
                      href={`data:text/markdown;charset=utf-8,${encodeURIComponent(markdown)}`}
                      download={`${report.projectName}-valoracion-ia.md`}
                      title="Descargar"
                    >
                      <i className="bi bi-download"></i>
                    </a>
                  ) : (
                    <button
                      className="btn btn-link btn-sm text-muted"
                      onClick={handleGenerateMarkdown}
                      disabled={generatingMarkdown}
                    >
                      {generatingMarkdown ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Generando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-stars me-1"></i>
                          Generar
                        </>
                      )}
                    </button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // --- Código legacy del editor/constructor eliminado del render activo ---
  // El código original (~500 líneas de JSX con drag-and-drop, citas, etc.)
  // está disponible en el historial de git: ver este mismo archivo antes
  // de la migración a la vista de descargas. Para reactivarlo, recupéralo
  // desde git y restaura el return principal.
  //
  // Se conserva la declaración de las funciones, estados y efectos del
  // editor más arriba en el fichero para no romper los hooks existentes,
  // pero su salida JSX ya no se renderiza.
};

export default AdminReportEditor;
